import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Base64File, ValidationResult } from '../models/fileValidation';

type ImageSource = File | Base64File | string | Blob;

@Injectable({
  providedIn: 'root'
})
export class FileValidationService {
  private config = {
    MAX_TOTAL_SIZE_MB: Number(environment.MAX_TOTAL_FILE_SIZE_MB || 10),
    MAX_IMAGE_SIZE_MB: Number(environment.MAX_IMAGE_SIZE_MB || 5),
    MAX_IMAGE_WIDTH: Number(environment.MAX_IMAGE_WIDTH || 1280),
    MAX_IMAGE_HEIGHT: Number(environment.MAX_IMAGE_HEIGHT || 1280),
    MAX_DOCUMENT_SIZE_MB: Number(environment.MAX_DOCUMENT_SIZE_MB || 5),
  };

  constructor() { }

  validateTotalFileSize(files: Array<File | Base64File>): ValidationResult {
    const maxSizeMB = this.config.MAX_TOTAL_SIZE_MB;
    
    if (!files || !Array.isArray(files) || files.length === 0) {
      return { valid: true, message: '' };
    }

    let totalSize = 0;
    
    files.forEach(file => {
      if (file instanceof File) {
        totalSize += file.size;
      } 
      else if ('base64Value' in file && file.base64Value) {
        const base64Size = (file.base64Value.length * 3) / 4 - 2;
        totalSize += base64Size;
      }
    });
    
    const totalSizeMB = totalSize / (1024 * 1024);
    
    if (totalSizeMB > maxSizeMB) {
      return {
        valid: false, 
        message: 'GENERAL.ERROR.TOTAL_FILE_SIZE_EXCEEDED',
        params: { maxSize: maxSizeMB }
      };
    }
    
    return { valid: true, message: '' };
  }

  validateSingleFileSize(file: File | Base64File): ValidationResult {
    const maxSizeMB = this.config.MAX_DOCUMENT_SIZE_MB;
    
    if (!file) {
      return { valid: true, message: '' };
    }
    
    let fileSize = 0;
    let fileName = 'File';
    
    if (file instanceof File) {
      fileSize = file.size;
      fileName = file.name;
    } 
    else if ('base64Value' in file && file.base64Value) {
      fileSize = (file.base64Value.length * 3) / 4 - 2;
      fileName = file.name || 'Image';
    }
    
    const fileSizeMB = fileSize / (1024 * 1024);
    
    if (fileSizeMB > maxSizeMB) {
      return {
        valid: false, 
        message: 'GENERAL.ERROR.FILE_SIZE_EXCEEDED',
        params: { fileName, maxSize: maxSizeMB }
      };
    }
    
    return { valid: true, message: '' };
  }

  validateImageDimensions(imageSource: ImageSource): Promise<ValidationResult> {
    const { MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT } = this.config;
    
    return new Promise((resolve) => {
      if (!imageSource) {
        resolve({ valid: true, message: '' });
        return;
      }
      
      const img = new Image();
      
      img.onload = () => {
        const { width, height } = img;
        
        if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT) {
          resolve({
            valid: false,
            message: 'GENERAL.ERROR.IMAGE_DIMENSIONS_EXCEEDED',
            params: { width, height, maxWidth: MAX_IMAGE_WIDTH, maxHeight: MAX_IMAGE_HEIGHT }
          });
        } else {
          resolve({ valid: true, message: '' });
        }
      };
      
      img.onerror = () => {
        resolve({ 
          valid: false, 
          message: 'GENERAL.ERROR.IMAGE_FORMAT_ERROR'
        });
      };
      
      if (typeof imageSource === 'string' && imageSource.startsWith('data:')) {
        img.src = imageSource;
      } else if (imageSource instanceof Blob || imageSource instanceof File) {
        img.src = URL.createObjectURL(imageSource);
      } else if (imageSource && typeof imageSource === 'object' && 'base64Value' in imageSource && typeof imageSource.base64Value === 'string') {
        img.src = imageSource.base64Value;
      } else {
        resolve({
          valid: false,
          message: 'GENERAL.ERROR.IMAGE_FORMAT_ERROR'
        });
      }
    });
  }

  async validateImage(file: File | Base64File): Promise<ValidationResult> {
    const sizeValidation = this.validateSingleFileSize(file);

    if (!sizeValidation.valid) {
      return sizeValidation;
    }
    
    const dimensionsValidation = await this.validateImageDimensions(file);
    return dimensionsValidation;
  }

  getFileLimits() {
    return { ...this.config };
  }
}